import { makeStyles, tokens, shorthands } from '@fluentui/react-components';

export const useIncidentActionsStyles = makeStyles({
  root: {
    display: 'flex',
    columnGap: tokens.spacingHorizontalXS,
    flexWrap: 'wrap',
    rowGap: tokens.spacingVerticalXXS,
  },
  commentsDialogContent: {
    width: '100%',
    minWidth: 'min(72vw, 520px)',
    display: 'flex',
    flexDirection: 'column',
  },
  commentsList: {
    maxHeight: '220px',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXS,
    marginBottom: tokens.spacingVerticalS,
  },
  commentItem: {
    ...shorthands.padding(tokens.spacingVerticalXS, tokens.spacingHorizontalS),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius(tokens.borderRadiusSmall),
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXXS,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  commentMessage: {
    lineHeight: tokens.lineHeightBase400,
    whiteSpace: 'pre-wrap',
  },
  commentTimestamp: {
    color: tokens.colorNeutralForeground3,
  },
  commentInput: {
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  sentinelLink: {
    textDecorationLine: 'none',
    color: tokens.colorBrandForegroundLink,
    ':hover': {
      textDecorationLine: 'underline',
    },
  },
});

export const useIncidentTableStyles = makeStyles({
  container: {
    width: '100%',
    minWidth: 0,
    overflowX: 'auto',
  },
  tableScrollHost: {
    width: '100%',
    minWidth: 0,
    overflowX: 'auto',
  },
  tableBase: {
    minWidth: '720px',
    '@media (max-width: 768px)': {
      minWidth: '640px',
    },
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalL),
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalM,
  },
  title: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
  },
  incidentCount: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground3,
  },
  wallScreenContainer: {
    ...shorthands.padding(tokens.spacingVerticalM, 0),
  },
  wallScreenHeaderCell: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalM),
  },
  headerLabelWithInfo: {
    display: 'inline-flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalXXS,
  },
  headerInfoButton: {
    minWidth: 'auto',
    height: '20px',
    width: '20px',
    color: tokens.colorNeutralForeground3,
    ':hover': {
      color: tokens.colorNeutralForeground2,
    },
  },
  wallScreenRow: {
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  editableCell: {
    minWidth: '100px',
    maxWidth: '120px',
    width: '110px',
  },
  compactEditableDropdown: {
    minWidth: '96px',
    maxWidth: '116px',
    width: '100%',
  },
  editableControlRow: {
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalXS,
    minWidth: 0,
  },
  savingSpinner: {
    flexShrink: 0,
  },
});

export const useIncidentTableToolbarStyles = makeStyles({
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.gap(tokens.spacingHorizontalM),
    ...shorthands.padding(tokens.spacingVerticalS, '0'),
    marginBottom: tokens.spacingVerticalM,
    '@media (max-width: 768px)': {
      alignItems: 'stretch',
      ...shorthands.gap(tokens.spacingVerticalS),
    },
  },
  searchBox: {
    flex: '1 1 260px',
    minWidth: '220px',
    '@media (max-width: 768px)': {
      flexBasis: '100%',
      minWidth: 0,
    },
  },
  searchInput: {
    width: '100%',
  },
  filters: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    ...shorthands.gap(tokens.spacingHorizontalS),
    '@media (max-width: 768px)': {
      width: '100%',
      ...shorthands.gap(tokens.spacingVerticalXS),
    },
  },
  filterGroup: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap(tokens.spacingHorizontalXS),
    '@media (max-width: 768px)': {
      width: '100%',
    },
  },
  filterIcon: {
    color: tokens.colorNeutralForeground3,
  },
  filterDropdown: {
    minWidth: '140px',
    '@media (max-width: 768px)': {
      flex: '1 1 100%',
      minWidth: 0,
      width: '100%',
    },
  },
  sortableHeader: {
    cursor: 'pointer',
    userSelect: 'none',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  noResults: {
    ...shorthands.padding(tokens.spacingVerticalXXL, '0'),
    color: tokens.colorNeutralForeground3,
  },
});

export const useGroupHeaderStyles = makeStyles({
  ownerSection: {
    marginBottom: tokens.spacingVerticalS,
  },
  ownerHeader: {
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
  },
  ownerHeaderContent: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap(tokens.spacingHorizontalS),
    width: '100%',
    flexWrap: 'wrap',
    rowGap: tokens.spacingVerticalXXS,
  },
  ownerIcon: {
    fontSize: tokens.fontSizeBase500,
    color: tokens.colorBrandForeground1,
  },
  incidentCount: {
    color: tokens.colorNeutralForeground3,
    marginLeft: 'auto',
  },
  ownerPanel: {
    paddingLeft: tokens.spacingHorizontalM,
    '@media (max-width: 768px)': {
      paddingLeft: tokens.spacingHorizontalS,
    },
  },
  severitySection: {
    marginBottom: tokens.spacingVerticalXS,
  },
  severityHeader: {
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.borderRadius(tokens.borderRadiusSmall),
  },
  severityHeaderContent: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap(tokens.spacingHorizontalS),
  },
  severityCount: {
    color: tokens.colorNeutralForeground3,
  },
});

export const useMobileCardStyles = makeStyles({
  mobileList: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalS),
    paddingBottom: tokens.spacingVerticalM,
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalS),
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalM),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow4,
  },
  cardTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalM,
  },
  cardOwner: {
    color: tokens.colorNeutralForeground2,
  },
  cardActions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  cardDetails: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalXS),
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
    paddingTop: tokens.spacingVerticalS,
  },
  cardDetailRow: {
    display: 'flex',
    ...shorthands.gap(tokens.spacingHorizontalS),
    alignItems: 'flex-start',
  },
  cardDetailLabel: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3,
    minWidth: '72px',
    flexShrink: 0,
  },
  cardDetailValue: {
    color: tokens.colorNeutralForeground1,
    wordBreak: 'break-word',
    flex: 1,
  },
  mobileEditableDropdown: {
    minWidth: 0,
    width: '100%',
    flex: 1,
  },
  editableControlRow: {
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalXS,
    minWidth: 0,
    width: '100%',
  },
  savingSpinner: {
    flexShrink: 0,
  },
});
